import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import automationImage from "@/assets/automation-workflow.jpg";

const FAQs = () => {
  const faqs = [
    {
      question: "What are AI Virtual Employees?",
      answer: "AI Virtual Employees are intelligent assistants powered by artificial intelligence that can handle a variety of business tasks such as customer service, scheduling, communication, and workflow automation. They work alongside your team to enhance efficiency and help you scale your operations without increasing headcount."
    },
    {
      question: "How much does it cost to implement AI Virtual Employees?",
      answer: "Pricing varies based on your specific needs, the complexity of the implementation, and the level of customization required. We offer flexible pricing models including monthly subscriptions and custom packages. Contact us for a personalized quote based on your business requirements."
    },
    {
      question: "How long does implementation take?",
      answer: "Implementation timelines vary depending on the scope of the project. Simple integrations can be up and running within a few weeks, while more complex, custom solutions may take 1-3 months. We work closely with you to establish a realistic timeline and ensure a smooth transition."
    },
    {
      question: "Can AI Virtual Employees integrate with our existing systems?",
      answer: "Yes! Our AI Virtual Employees are designed to integrate seamlessly with most common business tools including CRM systems, scheduling software, communication platforms, and more. During our consultation, we'll assess your current tech stack and design an integration strategy that works for your business."
    },
    {
      question: "What industries do you serve?",
      answer: "We specialize in serving healthcare providers, skilled trade companies, and small businesses. However, our solutions are adaptable to various industries. If you have specific needs, we're happy to discuss how our AI Virtual Employees can be tailored to your industry."
    },
    {
      question: "How customizable are the AI Virtual Employees?",
      answer: "Highly customizable! We build AI solutions tailored to your specific business needs, brand voice, and workflows. From the way they communicate with customers to the tasks they handle, everything can be customized to align with your business operations and values."
    },
    {
      question: "Will AI replace my human employees?",
      answer: "No. Our AI Virtual Employees are designed to augment, not replace, your human team. They handle repetitive, time-consuming tasks so your team can focus on high-value activities that require human judgment, creativity, and personal connection."
    },
    {
      question: "What kind of training is required?",
      answer: "We provide comprehensive onboarding and training for your team. The AI Virtual Employees are designed to be user-friendly, and we ensure your staff is comfortable working with them. Ongoing support is available as your needs evolve."
    },
    {
      question: "How do you ensure data security and privacy?",
      answer: "Data security is a top priority. We implement industry-standard security measures including encryption, secure data storage, and compliance with relevant regulations (such as HIPAA for healthcare clients). We're happy to discuss our security protocols in detail during consultation."
    },
    {
      question: "What kind of support do you provide after implementation?",
      answer: "We offer ongoing support to ensure your AI Virtual Employees continue to perform optimally. This includes technical support, performance monitoring, updates, and optimization as your business evolves. We're here to help you get the most value from your investment."
    },
    {
      question: "Can I see a demo before committing?",
      answer: "Absolutely! We encourage potential clients to book a demo so you can see our AI Virtual Employees in action and understand how they can benefit your specific business. Contact us to schedule your personalized demonstration."
    },
    {
      question: "What results can I expect?",
      answer: "Results vary by business and implementation, but common outcomes include: improved response times, increased customer satisfaction, reduced operational costs, better resource allocation, and the ability to scale without proportional increases in staffing. We work with you to define success metrics and track progress."
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        {/* Hero Section */}
        <section className="mb-16 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fade-in">
            Frequently Asked Questions
          </h1>
          <div className="h-1 w-24 bg-accent mx-auto mb-8"></div>
          <p className="text-lg md:text-xl text-muted-foreground animate-fade-in">
            Find answers to common questions about our AI Virtual Employees and services.
          </p>
        </section>

        {/* Feature Image */}
        <section className="mb-12 rounded-2xl overflow-hidden">
          <img
            src={automationImage}
            alt="AI Workflow Automation"
            className="w-full h-[300px] object-cover"
          />
        </section>

        {/* FAQs Accordion */}
        <section className="mb-16">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="border-2 rounded-lg px-6 hover:border-accent transition-colors"
              >
                <AccordionTrigger className="text-left hover:no-underline hover:text-accent">
                  <span className="font-semibold">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pt-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-primary text-primary-foreground rounded-2xl p-8 md:p-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Still Have Questions?
          </h2>
          <p className="text-lg mb-8 opacity-90">
            We're here to help! Contact us directly and we'll be happy to answer any questions you have.
          </p>
          <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-primary font-semibold">
            <Link to="/contact">
              Get in Touch <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </section>
      </div>
    </div>
  );
};

export default FAQs;
